import { IImageStorage } from './imagestorage/imageStorage'
import { DexieImageStorage } from './imagestorage/'
import { ICacheManager } from './assets/AssetCacheManager'
import IClient, { IClientOptions } from './index'
import { IApi } from './api/api'
import { AssetType } from './assets/AssetCacheManager'
import { withTimeout } from '../utils'
import { InMemoryCacheManager } from './assets/InMemoryCacheManager'
import { logError } from '../logging'
import { ISettings } from '../settings/settings'
import { IAttachment, IChecklist } from '../models/Checklist'
import { IAttachmentResponse, IIDAndName } from '../models/Entity'
import {
  IStoredTicket,
  ITicket,
  ITicketCategories,
  ITicketTypes
} from '../models/Ticket'
import { ITags } from '../models/Franchise'
import { IUserCapabilities } from '../models/User'
import { IIncidentEmail, IIncidentEmailResponse } from '../models/Incident'
import { TemplateType } from '../models/Template'

interface IResult {
  response: string
}
// This is the client for the Compliance-manager app
export class Client implements IClient {
  imageStorage: IImageStorage
  api: IApi
  defaultTimeout: number
  cache: ICacheManager<IResult>
  defaultSettings: ISettings

  constructor(options: IClientOptions) {
    //    this.cacheManager =
    //      options.cacheManager ||
    //      new NoCacheManager(
    //        () => true,
    //        () => true
    //      )
    this.defaultSettings = options.settings
    this.imageStorage = options.imageStorage || new DexieImageStorage()
    this.api = options.api
    this.defaultTimeout = options.defaultTimeout
      ? options.defaultTimeout
      : 180000
    this.cache = new InMemoryCacheManager<IResult>(
      () => true,
      () => true
    )
  }

  getAuthenticatedPhotoUrl(url: string): string {
    return this.api.getAuthenticatedPhotoUrl(url)
  }

  async saveAttachment(attachment: IAttachment): Promise<IAttachmentResponse> {
    return this.api.saveAttachment(attachment)
  }

  viewAttachment(attachment: IAttachment): void {
    this.api.viewAttachment(attachment)
  }

  async deleteAttachment(attachment: IAttachment): Promise<any> {
    return this.api.deleteAttachment(attachment)
  }

  // returns a url that could be cached
  async getLocalAssetUrl(
    type: AssetType,
    webUrl: string,
    _accessToken?: string,
    createObjectUrl?: boolean
  ): Promise<string> {
    let cacheLocalUrl: string | null

    // Use the full URL as the cache key
    const cacheKey = `${type}_${webUrl}`
    const httpOptions = {}
    const localUrl = this.cache.get(cacheKey)
    if (localUrl) {
      cacheLocalUrl = await this.imageStorage.getImageObjectUrl(
        localUrl.response
      )
      console.log('cached: ', cacheLocalUrl)
    } else {
      try {
        const res = await withTimeout(
          this.defaultTimeout,
          fetch(webUrl, httpOptions)
        )
        if (res.ok) {
          const data = await res.blob()
          cacheLocalUrl = await this.imageStorage.storeImage(data, 'temporary')
          this.cache.add(cacheKey, { response: cacheLocalUrl } as IResult)
          console.log('newly cached: ', cacheKey)
          console.log('newly cached cacheLocalUrl: ', cacheLocalUrl)
        } else {
          throw new Error(
            `Got status ${res.status} while downloading ${webUrl}`
          )
        }
      } catch (e) {
        logError(e)
        throw new Error(e)
      }
    }
    if (createObjectUrl) {
      return await this.imageStorage.getImageObjectUrl(cacheLocalUrl)
    } else {
      return cacheLocalUrl
    }
  }

  getSettings(): Promise<ISettings> {
    // fake promise as this will be stored in a database
    return new Promise((resolve) => {
      resolve(this.defaultSettings)
    })
  }

  saveIncident(
    _incidentData: FormData,
    _incidentId?: number | null,
    _franchiseeId?: number,
    _franchiseId?: number
  ): Promise<number> {
    throw new Error('Method not implemented.')
  }

  async getIncidentEmail(
    incidentId: number,
    franchiseId: number,
    franchiseeId: number,
    templateType?: TemplateType
  ): Promise<IIncidentEmailResponse> {
    return this.api.getIncidentEmail(
      incidentId,
      franchiseId,
      franchiseeId,
      templateType
    )
  }

  sendIncidentEmail(
    incidentId: number,
    incidentEmail: IIncidentEmail,
    franchiseId: number,
    franchiseeId: number
  ): Promise<void> {
    return this.api.sendIncidentEmail(
      incidentId,
      incidentEmail,
      franchiseId,
      franchiseeId
    )
  }

  getHazardTickets(
    _franchiseId: number,
    _hazardId: number,
    _fromId: number
  ): Promise<ITicket[]> {
    throw new Error('Method not implemented.')
  }

  getIncidentTickets(
    _franchiseId: number,
    _incidentId: number,
    _fromId: number
  ): Promise<ITicket[]> {
    throw new Error('Method not implemented.')
  }

  getCapabilities(): IUserCapabilities {
    throw new Error('Method not implemented.')
  }

  getTicketCategories(_franchiseId?: number): Promise<ITicketCategories> {
    throw new Error('Method not implemented (FROM DATA).')
  }

  getTicketTypes(_franchiseId?: number): Promise<ITicketTypes> {
    throw new Error('Method not implemented (FROM DATA).')
  }

  getFranchiseTags(_franchiseId?: number): Promise<ITags> {
    throw new Error('Method not implemented (FROM DATA).')
  }

  getChecklistTemplate(
    _franchiseId: number,
    _franchiseeId: number,
    _versionId: number,
    _request_type?: 'ticket' | 'checklist'
  ): Promise<IChecklist> {
    throw new Error('Method not implemented (FROM DATA).')
  }

  saveLocalTicket(_storedTicket: IStoredTicket): Promise<number> {
    throw new Error('Method not implemented (FROM DATA).')
  }

  removeLocalTicket(_id: number): Promise<void> {
    throw new Error('Method not implemented.')
  }

  saveTicket(
    _franchiseId: number,
    _franchiseeId: number,
    _ticketData: FormData,
    _timeout: number,
    _ticketId?: number
  ): Promise<number> {
    throw new Error('Method not implemented.')
  }

  searchHazardByName(
    _name: string,
    _from: number,
    _howMany: number,
    _franchiseId?: number
  ): Promise<IIDAndName[]> {
    throw new Error('Method not implemented.')
  }

  saveIncidentV2(
    _franchiseId: number,
    _incident: IChecklist,
    _builderType: TemplateType,
    _isExistingIncident?: boolean
  ): Promise<{ incidentId: number }> {
    throw new Error('Method not implemented.')
  }
}
export default Client
